import { useCallback, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Button,
  ButtonVariants,
  ButtonSizes,
  BadgeVariants,
  RowActionsMenu,
} from '@carboledger/ui';
import { MoreVertical } from '@carboledger/ui/icons';
import { useTranslations } from '@/hooks';
import { ROUTES } from '@/app/routes';
import { LoadingOverlay, ConfirmDialog, EmptyState } from '@/components/feedback';
import { DiscrepancyModal, DetailPageHeader } from '@/components/common';
import {
  useGetContractDetailQuery,
  useGetLinkedShipmentsQuery,
  useDeleteContractMutation,
  useUpdateContractStatusMutation,
} from '../api/contractsApi';
import { buildContractDiscrepancySections } from '../constants/contractDiscrepancy';
import {
  buildContractActions,
  resolveContractAction,
  resolveContractMenuItems,
  nextContractStatus,
} from '../constants/contractActions';
import { CONTRACT_STATUS_TONE } from '@/config/statusTones';
import { ContractOverviewTabView } from '../components/ContractOverviewTabView';
import { LinkedShipmentsTabView } from '../components/LinkedShipmentsTabView';
import {
  AddAttachmentModal,
  AttachmentsTabView,
  LinkShipmentsForContract,
  buildContractDetailLinkFields,
} from '@/components/common';
import { ATTACHMENT_TARGETS } from '@/lib/api/attachmentsApi';
import { EMPTY_MAP } from '@/lib/utils';
import styles from './ContractDetailPage.module.scss';

const DetailTab = Object.freeze({
  overview: 'overview',
  shipments: 'shipments',
  attachments: 'attachments',
});

// Inbounds ▸ Contracts ▸ Contract details.
export const ContractDetailPage = () => {
  const t = useTranslations();
  const navigate = useNavigate();
  const { module, contractId } = useParams();
  const [activeTab, setActiveTab] = useState(DetailTab.overview);
  const [isDiscrepancyOpen, setIsDiscrepancyOpen] = useState(false);
  const [isLinkOpen, setIsLinkOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [deleteContract] = useDeleteContractMutation();
  const [updateContractStatus] = useUpdateContractStatusMutation();

  const {
    data: contractDetail,
    isLoading: isLoadingContract,
    isError: isContractError,
  } = useGetContractDetailQuery(contractId);

  const [shipmentFilters, setShipmentFilters] = useState(EMPTY_MAP);

  const { data: linkedShipments } = useGetLinkedShipmentsQuery(
    {
      id: contractId,
      filters: shipmentFilters,
      contractBusinessId: contractDetail?.contractId,
      workingUnit: contractDetail?.workingUnit,
      declarationUnit: contractDetail?.declarationUnit,
    },
    { skip: !contractDetail || activeTab !== DetailTab.shipments },
  );

  const tabs = useMemo(
    () => [
      { value: DetailTab.overview, label: t('SECTION_OVERVIEW') },
      { value: DetailTab.shipments, label: t('CONTRACTS_DETAIL_TAB_SHIPMENTS') },
      { value: DetailTab.attachments, label: t('SECTION_ATTACHMENTS') },
    ],
    [t],
  );

  const goToContracts = useCallback(() => navigate(ROUTES.contracts(module)), [navigate, module]);

  const breadcrumbItems = useMemo(
    () => [
      { label: t('CONTRACTS_TITLE'), onClick: () => navigate(ROUTES.contracts(module)) },
      { label: contractDetail?.contractId ?? '-' },
    ],
    [t, navigate, module, contractDetail?.contractId],
  );

  const handleLinkShipment = useCallback(() => setIsLinkOpen(true), []);
  const handleCloseLink = useCallback(() => setIsLinkOpen(false), []);
  const handleReceiveShipment = useCallback(() => {
    navigate(ROUTES.contractReceiveShipment(module, contractId));
  }, [navigate, module, contractId]);
  const handleOpenDiscrepancies = useCallback(() => setIsDiscrepancyOpen(true), []);
  const handleCloseDiscrepancies = useCallback(() => setIsDiscrepancyOpen(false), []);
  const handleEditContract = useCallback(() => {
    navigate(ROUTES.contractEdit(module, contractId));
  }, [navigate, module, contractId]);
  const handleToggleClosed = useCallback(
    (row) => {
      updateContractStatus({ module, id: contractId, status: nextContractStatus(row) });
    },
    [updateContractStatus, module, contractId],
  );
  const [isAttachmentOpen, setIsAttachmentOpen] = useState(false);
  const handleAddAttachment = useCallback(() => setIsAttachmentOpen(true), []);
  const handleCloseAttachment = useCallback(() => setIsAttachmentOpen(false), []);

  const handleDeleteContract = useCallback(() => setIsDeleteOpen(true), []);
  const handleCancelDelete = useCallback(() => setIsDeleteOpen(false), []);
  const handleConfirmDelete = useCallback(async () => {
    const result = await deleteContract({ module, id: contractId });
    setIsDeleteOpen(false);
    // The contract is gone, so its detail route is too.
    if (!result?.error) navigate(ROUTES.contracts(module));
  }, [deleteContract, navigate, module, contractId]);

  const contractActions = useMemo(
    () =>
      buildContractActions(t, {
        onReceiveShipment: handleReceiveShipment,
        onLinkShipment: handleLinkShipment,
        onAddAttachment: handleAddAttachment,
        onToggleClosed: handleToggleClosed,
        onEdit: handleEditContract,
        onDelete: handleDeleteContract,
      }),
    [
      t,
      handleReceiveShipment,
      handleLinkShipment,
      handleAddAttachment,
      handleToggleClosed,
      handleEditContract,
      handleDeleteContract,
    ],
  );

  const linkAction = useMemo(
    () => resolveContractAction(contractActions.linkShipment, contractDetail),
    [contractActions, contractDetail],
  );
  const receiveAction = useMemo(
    () => resolveContractAction(contractActions.receiveShipment, contractDetail),
    [contractActions, contractDetail],
  );
  const menuItems = useMemo(
    () => resolveContractMenuItems(contractActions, contractDetail),
    [contractActions, contractDetail],
  );

  const headerActions = useMemo(
    () => (
      <>
        <Button
          variant={ButtonVariants.secondary}
          size={ButtonSizes.sm}
          disabled={linkAction.disabled}
          onClick={linkAction.onSelect}
        >
          {linkAction.label}
        </Button>
        <Button
          variant={ButtonVariants.primary}
          size={ButtonSizes.sm}
          disabled={receiveAction.disabled}
          onClick={receiveAction.onSelect}
        >
          {receiveAction.label}
        </Button>
        <RowActionsMenu
          items={menuItems}
          ariaLabel={t('COMMON_MORE_ACTIONS')}
          icon={MoreVertical}
        />
      </>
    ),
    [t, linkAction, receiveAction, menuItems],
  );

  const metaFields = useMemo(() => {
    if (!contractDetail) return [];
    return [
      {
        id: 'material',
        label: t('FIELD_MATERIAL_NAME'),
        value: contractDetail.materialName,
      },
      { id: 'sentBy', label: t('FIELD_SENT_BY'), value: contractDetail.supplierName },
      {
        id: 'receivedAt',
        label: t('FIELD_RECEIVED_AT'),
        value: contractDetail.facilityName,
      },
      {
        id: 'terminal',
        label: t('FIELD_TERMINAL_NAME'),
        value: contractDetail.terminal,
      },
      {
        id: 'transport',
        label: t('CONTRACTS_DETAIL_TRANSPORT_NAME'),
        value: contractDetail.transportName,
      },
      { id: 'vessel', label: t('FIELD_VESSEL_NAME'), value: contractDetail.vesselName },
      {
        id: 'status',
        label: t('FIELD_STATUS'),
        value: contractDetail.statusLabel,
        type: 'status',
        tone: CONTRACT_STATUS_TONE[contractDetail.statusCode] ?? BadgeVariants.default,
      },
      {
        id: 'dispatchBy',
        label: t('FIELD_DISPATCH_BY'),
        value: contractDetail.dispatchBy,
      },
      {
        id: 'discrepancies',
        label: t('FIELD_DISCREPANCIES'),
        count: contractDetail.discrepancyCount,
        type: 'discrepancy',
        ariaLabel: t('ACTION_VIEW_DISCREPANCIES'),
        onClick: handleOpenDiscrepancies,
      },
    ];
  }, [t, contractDetail, handleOpenDiscrepancies]);

  const discrepancySections = useMemo(
    () => buildContractDiscrepancySections(t, contractDetail),
    [t, contractDetail],
  );

  const linkDetailFields = useCallback(
    (unitType, assignedInUnit) =>
      buildContractDetailLinkFields(t, contractDetail, unitType, assignedInUnit),
    [t, contractDetail],
  );

  if (isLoadingContract) {
    return <LoadingOverlay label={t('COMMON_LOADING')} />;
  }

  if (isContractError || !contractDetail) {
    return (
      <EmptyState
        title={t('CONTRACTS_LOAD_ERROR')}
        action={
          <Button variant={ButtonVariants.secondary} onClick={goToContracts}>
            {t('COMMON_BACK')}
          </Button>
        }
      />
    );
  }

  return (
    <div className={styles.page}>
      <DetailPageHeader
        breadcrumbItems={breadcrumbItems}
        title={contractDetail.contractId}
        actions={headerActions}
        metaFields={metaFields}
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        tabsAriaLabel={t('CONTRACTS_TITLE')}
      />

      <div className={styles.content}>
        {activeTab === DetailTab.overview && <ContractOverviewTabView contract={contractDetail} />}

        {activeTab === DetailTab.shipments && (
          <LinkedShipmentsTabView
            module={module}
            contractId={contractId}
            contractBusinessId={contractDetail.contractId}
            linkedShipments={linkedShipments}
            filters={shipmentFilters}
            onFilterChange={setShipmentFilters}
          />
        )}

        {activeTab === DetailTab.attachments && (
          <AttachmentsTabView
            attachments={contractDetail.attachments}
            target={ATTACHMENT_TARGETS.contract}
            module={module}
            entityId={contractId}
            modalTitle={t('CONTRACTS_ADD_ATTACHMENT_MODAL_TITLE', {
              contractId: contractDetail.contractId,
            })}
            addLabel={t('ACTION_ADD_ATTACHMENTS')}
          />
        )}
      </div>

      <LinkShipmentsForContract
        module={module}
        contractDbId={contractId}
        contractLabel={contractDetail.contractId}
        detailFields={linkDetailFields}
        open={isLinkOpen}
        onClose={handleCloseLink}
      />

      <DiscrepancyModal
        open={isDiscrepancyOpen}
        onClose={handleCloseDiscrepancies}
        referenceId={contractDetail.contractId}
        discrepancies={contractDetail.discrepancies}
        sections={discrepancySections}
      />

      <AddAttachmentModal
        open={isAttachmentOpen}
        onClose={handleCloseAttachment}
        title={t('CONTRACTS_ADD_ATTACHMENT_MODAL_TITLE', { contractId: contractDetail.contractId })}
        target={ATTACHMENT_TARGETS.contract}
        module={module}
        entityId={contractId}
      />

      <ConfirmDialog
        open={isDeleteOpen}
        title={t('CONTRACTS_DELETE_TITLE')}
        bodyTitle={t('CONTRACTS_DELETE_BODY_TITLE', { contractId: contractDetail.contractId })}
        description={t('CONTRACTS_DELETE_DESCRIPTION')}
        confirmLabel={t('COMMON_DELETE')}
        tone="danger"
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
      />
    </div>
  );
};
